import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  Switch,
  TextField,
  Button,
  CircularProgress,
  Alert
} from "@mui/material";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import SendIcon from "@mui/icons-material/Send";

export default function AIAutoCommentPage({ token }) {
  const [config, setConfig] = useState({
    isEnabled: false,
    knowledgeBase: "",
    replyCount: 0
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const [sandboxMessage, setSandboxMessage] = useState("");
  const [sandboxReply, setSandboxReply] = useState("");
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    fetchConfig();
  }, [token]);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/user/ai-auto-comment-config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data?.aiAutoCommentConfig) {
        setConfig(res.data.aiAutoCommentConfig);
      }
    } catch (err) {
      console.error("Failed to fetch config", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveSuccess(false);
    try {
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/user/ai-auto-comment-config`, {
        isEnabled: config.isEnabled,
        knowledgeBase: config.knowledgeBase
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error("Failed to save config", err);
    } finally {
      setSaving(false);
    }
  };

  const handleTestSandbox = async () => {
    if (!sandboxMessage) return;
    setTesting(true);
    try {
      const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/ai/draft-auto-reply`, {
        userMessage: sandboxMessage,
        knowledgeBase: config.knowledgeBase
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSandboxReply(res.data.reply || "No reply generated.");
    } catch (err) {
      console.error("Sandbox test failed", err);
      setSandboxReply("Error generating test reply.");
    } finally {
      setTesting(false);
    }
  };

  if (loading) return <div className="flex justify-center p-8"><CircularProgress /></div>;

  return (
    <Box sx={{ pb: 6 }}>
      {/* Header */}
      <div className="flex items-center gap-2 mb-2">
        <SmartToyIcon className="text-primary-600 dark:text-primary-400" />
        <Typography variant="h6" className="text-xl font-bold text-gray-900 dark:text-white">
          Auto Comment (AI Assistant)
        </Typography>
      </div>
      <p className="text-gray-600 dark:text-gray-400 max-w-3xl mb-8 text-base">
        Teach your AI assistant! Paste your store policies, shipping times, and FAQs below. 
        Whenever a customer sends a DM or comment, your AI will try to answer based <strong>only</strong> on this text.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch">
        {/* Left: Configuration */}
        <div className="card-modern p-6 flex flex-col h-full">
          <div className="flex justify-between items-center mb-1">
            <Typography variant="subtitle1" className="text-lg font-bold text-gray-900 dark:text-white">
              AI Auto-Pilot
            </Typography>
            <Switch
              checked={config.isEnabled}
              onChange={(e) => setConfig({ ...config, isEnabled: e.target.checked })}
              color="primary"
            />
          </div>
          <Typography variant="body2" className="text-gray-600 dark:text-gray-400 mb-6">
            {config.isEnabled 
              ? "Active: The AI will automatically reply to incoming messages." 
              : "Paused: The AI is currently disabled and will not reply."}
          </Typography>

          {config.replyCount >= 100 ? (
            <div className="mb-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl">
              <Typography className="text-sm text-yellow-800 dark:text-yellow-200">
                ⚠️ Current Usage: <strong>{config.replyCount || 0} / 100</strong> Automated Replies (Testing Limit Reached)
              </Typography>
            </div>
          ) : (
            <div className="mb-6 p-4 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-xl">
              <Typography className="text-sm text-primary-800 dark:text-primary-200">
                ℹ️ Current Usage: <strong>{config.replyCount || 0} / 100</strong> Automated Replies (Testing Limit)
              </Typography>
            </div>
          )}

          <Typography variant="subtitle1" className="font-bold text-gray-900 dark:text-white mb-2">
            Knowledge Base
          </Typography>
          <TextField
             fullWidth
             multiline
             rows={12}
             variant="outlined"
             placeholder="Paste your FAQs, shipping policy, pricing, etc. here..."
             value={config.knowledgeBase}
             onChange={(e) => setConfig({ ...config, knowledgeBase: e.target.value })}
             className="mb-4 bg-white dark:bg-gray-800 rounded-lg"
          />

          <div className="mt-auto pt-2">
            <Button 
              variant="contained" 
              color="primary" 
              onClick={handleSave} 
              disabled={saving}
              className="w-full sm:w-auto normal-case shadow-sm py-2 px-6 font-semibold"
              disableElevation
            >
               {saving ? "Saving..." : "Save Configuration"}
            </Button>
            {saveSuccess && (
              <Typography variant="body2" className="text-green-600 dark:text-green-400 mt-2 block">
                Settings saved successfully!
              </Typography>
            )}
          </div>
        </div>

        {/* Right: Sandbox Tester */}
        <div className="card-modern p-6 flex flex-col h-full">
          <Typography variant="subtitle1" className="text-lg font-bold text-gray-900 dark:text-white mb-1">
            AI Sandbox Tester
          </Typography>
          <p className="text-gray-600 dark:text-gray-400 mb-6 text-sm">
            Ask a test question to see exactly how the AI will respond based on the Knowledge Base you configured.
          </p>

          <TextField
             fullWidth
             variant="outlined"
             label="Customer Message (Test)"
             value={sandboxMessage}
             onChange={(e) => setSandboxMessage(e.target.value)}
             className="mb-4 bg-white dark:bg-gray-800 rounded-lg"
          />

          <div className="mt-4">
            <Button 
              variant="outlined" 
              color="primary" 
              endIcon={<SendIcon />} 
              onClick={handleTestSandbox}
              disabled={testing || !sandboxMessage}
              className="mb-6 normal-case py-2 px-6 font-semibold"
            >
               {testing ? "Analyzing..." : "Test AI"}
            </Button>
          </div>

          {sandboxReply && (
            <div className="p-4 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-700 rounded-xl mt-auto">
               <Typography variant="subtitle2" className="text-primary-600 dark:text-primary-400 font-bold mb-2">
                 AI Response:
               </Typography>
               <Typography variant="body1" className="text-gray-800 dark:text-gray-200">
                 {sandboxReply}
               </Typography>
               {sandboxReply === "UNKNOWN" && (
                 <Typography variant="caption" className="text-red-500 block mt-2">
                   (The AI didn't know the answer. In live mode, this DM will remain unread for you to answer.)
                 </Typography>
               )}
            </div>
          )}
        </div>
      </div>
    </Box>
  );
}
